import { NavLink, useNavigate } from "react-router-dom";
import { BugIcon, LayoutGridIcon, PlusIcon, SettingsIcon } from "lucide-react";

import { useProjects } from "../app/queries";
import { useAuth } from "../app/AuthContext";
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";
import { Button } from "./ui/button";

export function Sidebar() {
  const navigate = useNavigate();
  const { hasToken } = useAuth();
  const { isMobile, setOpenMobile } = useSidebar();
  const projects = useProjects({ enabled: hasToken });
  const closeMobile = () => isMobile && setOpenMobile(false);

  return (
    <SidebarRoot aria-label="Projects navigation">
      <SidebarHeader className="border-b border-sidebar-border">
        <NavLink
          to="/"
          onClick={closeMobile}
          className="flex h-10 items-center gap-3 rounded-md px-2 hover:bg-sidebar-accent"
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground [&_svg]:size-4">
            <BugIcon />
          </span>
          <span className="min-w-0 leading-tight group-data-[collapsible=icon]/sidebar-wrapper:hidden">
            <span className="block truncate text-sm font-semibold tracking-tight">
              Mushi
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              Bug reporting
            </span>
          </span>
        </NavLink>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                render={
                  <NavLink
                    to="/"
                    end
                    onClick={closeMobile}
                    title="All projects"
                    data-testid="nav-all-projects"
                  />
                }
              >
                <LayoutGridIcon />
                <span>All projects</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                render={
                  <NavLink
                    to="/settings/mcp"
                    onClick={closeMobile}
                    title="MCP access"
                    data-testid="nav-settings-mcp"
                  />
                }
              >
                <SettingsIcon />
                <span>MCP access</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup>
          <div className="flex items-center justify-between pr-1 group-data-[collapsible=icon]/sidebar-wrapper:hidden">
            <SidebarGroupLabel>Projects</SidebarGroupLabel>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              aria-label="Create project"
              onClick={() => {
                navigate("/projects/new");
                closeMobile();
              }}
            >
              <PlusIcon />
            </Button>
          </div>
          <SidebarMenu>
            {(() => {
              const projectList = projects.data?.projects ?? [];
              if (projects.isLoading) {
                return (
                  <li className="px-2 py-2 text-xs text-muted-foreground group-data-[collapsible=icon]/sidebar-wrapper:sr-only">
                    Loading…
                  </li>
                );
              }
              if (projects.isError) {
                return (
                  <li className="px-2 py-2 text-xs text-destructive group-data-[collapsible=icon]/sidebar-wrapper:sr-only">
                    {projects.error.message}
                  </li>
                );
              }
              if (projectList.length === 0) {
                return (
                  <li className="px-2 py-3 text-xs leading-relaxed text-muted-foreground group-data-[collapsible=icon]/sidebar-wrapper:sr-only">
                    No projects yet. Create one to start receiving reports.
                  </li>
                );
              }
              return projectList.map((project) => (
                <SidebarMenuItem key={project.id}>
                  <SidebarMenuButton
                    render={
                      <NavLink
                        to={`/projects/${project.id}`}
                        onClick={closeMobile}
                        title={project.name}
                        data-testid={`nav-project-${project.id}`}
                      />
                    }
                  >
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ background: project.color }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {project.name}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                      {project.key}
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ));
            })()}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <p className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 group-data-[collapsible=icon]/sidebar-wrapper:sr-only">
          {(() => {
            const count = projects.data?.projects.length ?? 0;
            return `v1.0.0 · ${count} ${count === 1 ? "project" : "projects"}`;
          })()}
        </p>
      </SidebarFooter>
    </SidebarRoot>
  );
}
